<template>
  <b-row>
    <b-col cols="12" style="padding: 8px;">
      <strong>
        <span class="sidebar-title">Infrastructures</span>
      </strong>
      <i
        class="fas fa-info-circle"
        v-b-popover.hover.bottom="'Select desired infrastructures by selecting them below.'"
        title="Select Infrastructures"
      ></i>
      <b-row>
        <b-col cols="9" class="p-0">
          <span style="font-size: 0.8rem">Show Infrastructures on map</span>
        </b-col>
        <b-col cols="3" style="text-align: right !important;">
          <toggle-button v-model="toggleValue" :labels="true" />
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
          class="p-0"
        >
          <b-form-checkbox
            v-for="infra in uniqueInfrastructures"
            v-model="selectedInfraList"
            :key="infra.name"
            :value="infra.name"
            :id="infra.name"
            class="instrument-check"
          >
            <b-row>
              <b-col cols="10" class="p-0 text-left">
                <!--Infra icons in separate component displayed on infra popup in map-->
                <infra-icons :infraAT="infra.asset_type"></infra-icons>
                <span>{{infra.name}}</span>
              </b-col>
              <b-col cols="2" class="p-0 pr-1 text-right">
                <b-badge variant="light">{{infra.count}}</b-badge>
              </b-col>
            </b-row>
          </b-form-checkbox>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
/**
 * Infrastructure component
 *
 * This components contains all the interactions for the
 * infrastructure section of the map sidebar
 */
/* eslint-disable vue/multi-word-component-names */
import _ from 'lodash';
import { mapState, mapActions, mapMutations } from 'vuex';
import InfraIcons from './infraIcons';

export default {
  name: 'Infrastructures',
  data() {
    return {
      infraCounts: {},
      toggleValue: true,
      lastSelectedInfra: [],
    };
  },
  components: {
    'infra-icons': InfraIcons,
  },
  mounted() {
    this.updateInfraCheckbox([]);
  },
  methods: {
    ...mapActions(['setSelectedInfra']),
    ...mapMutations([
      'updateInfraCheckbox', /** map action, saves the infra checkbox to store */
    ]),
    /**
     * Gets the count of each infrastructure
     */
    getInfraCounts() {
      this.cleanedInfrastructures.forEach((infra) => {
        if (_.has(this.infraCounts, infra.name)) {
          this.infraCounts[infra.name] += 1;
        } else {
          this.infraCounts[infra.name] = 1;
        }
      });
    },
  },
  computed: {
    ...mapState({
      infrastructures: (state) => state.infrastructures,
      webmap: (state) => state.webmap,
      instCheckboxes: (state) => state.instCheckboxSelection,
    }),
    /**
     * Sets up a getter and setter for the list of selected
     * infrastructures.
     */
    selectedInfraList: {
      get() {
        return this.$store.state.selectedInfraList;
      },
      set(value) {
        this.setSelectedInfra({ selected: value });
      },
    },
    /**
     * Computed() -  Maps through the listing of infrastructures and
     * cleans up the name. No reference designator is passed through
     */
    cleanedInfrastructures() {
      return this.infrastructures.map((infra) => ({
        ...infra,
        name: infra.name.replace(/ *\([^)]*\) */g, ''),
      }));
    },
    /**
     * Computed() - Goes through the cleaned up list of infrastructures
     * and maps the count of each unique infrastructure
     */
    uniqueInfrastructures() {
      /** Computed count mapping of all the unique infrastructures */
      this.getInfraCounts();
      const uniqueInfra = _.sortBy(_.uniqBy(this.cleanedInfrastructures, 'name'), 'name');
      return uniqueInfra.map((infra) => ({
        ...infra,
        count: this.infraCounts[infra.name],
      }));
    },
    /**
     * Computed() - Maps the unique infrastructure names (not object)
     */
    infraNames() {
      return this.uniqueInfrastructures.map((infra) => infra.name);
    },
  },
  watch: {
    /**
     * Watcher() - Used to update the value of the selected infra checkboxes
     * which will update the layout property of the map icons according to
     * the selected infrastructures.
     * @param {array} newVal - array of infrastructure checkboxes
     */
    selectedInfraList(newVal) {
      if (newVal.length > 0 || this.instCheckboxes.length > 0) {
        this.webmap.setLayoutProperty('infra-icon', 'visibility', 'none');
        this.webmap.setLayoutProperty('instrument-layers-point', 'visibility', 'none');
        this.webmap.setLayoutProperty('instrument-layers-clusters', 'visibility', 'none');
        this.webmap.setLayoutProperty('cluster-text', 'visibility', 'none');
        this.webmap.setLayoutProperty('mooring-circle', 'visibility', 'none');
        this.webmap.setLayoutProperty('mooring-text', 'visibility', 'none');
        this.updateInfraCheckbox(newVal);
      } else if (newVal.length === 0 && this.instCheckboxes.length === 0) {
        this.webmap.setLayoutProperty('infra-icon', 'visibility', 'visible');
        this.webmap.setLayoutProperty('instrument-layers-point', 'visibility', 'visible');
        this.webmap.setLayoutProperty('instrument-layers-clusters', 'visibility', 'visible');
        this.webmap.setLayoutProperty('cluster-text', 'visibility', 'visible');
        this.webmap.setLayoutProperty('mooring-circle', 'visibility', 'visible');
        this.webmap.setLayoutProperty('mooring-text', 'visibility', 'visible');
        this.updateInfraCheckbox(newVal);
      }
    },
    /**
     * Watcher() - Used for the 'show all' infrastructure toggle button
     * on top of the infrastructure list.
     * @param {boolean} newVal - toggle value
     */
    toggleValue(newVal) {
      if (newVal === false) {
        this.webmap.setLayoutProperty('infra-icon', 'visibility', 'none');
      } else if (newVal === true) {
        this.webmap.setLayoutProperty('infra-icon', 'visibility', 'visible');
      }
    },
  },
};
</script>

<style>
.infrastructure-check {
  display: inline-block;
}
.instrument-check label.custom-control-label {
  width: 100% !important;
}
</style>
